import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaImage, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

const SimpleCourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_type: 'free', // 'free' or 'paid'
    price: 0,
    start_date: '',
    end_date: '',
    start_time: '',
    duration: '',
    location_type: 'online', // 'online' or 'onsite'
    location: '',
    max_enrollment: '',
    instructor: '',
    status: 'published'
  });

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/courses/simple`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data.data || []);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        console.log('✅ Image preview loaded');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If course_type is changed to 'free', automatically set price to 0
    if (name === 'course_type' && value === 'free') {
      setFormData(prev => ({
        ...prev,
        course_type: 'free',
        price: 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_type: 'free',
      price: 0,
      start_date: '',
      end_date: '',
      start_time: '',
      duration: '',
      location_type: 'online',
      location: '',
      max_enrollment: '',
      instructor: '',
      status: 'published'
    });
    setImageFile(null);
    setImagePreview('');
    setEditingCourse(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      // Step 1: Upload image if selected
      let imagePath = editingCourse?.course_image || '';

      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);

        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        if (!BACKEND_URL) {
          throw new Error('Backend URL not configured');
        }
        const uploadUrl = `${BACKEND_URL}/api/admin-upload/courses/image`;

        const imageResponse = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Do NOT set Content-Type - let browser set it with boundary
          },
          body: imageFormData
        });

        if (imageResponse.ok) {
          const imageData = await imageResponse.json();
          imagePath = imageData.data.imagePath;
          console.log('✅ Image uploaded successfully:', imagePath);
        } else {
          const errorData = await imageResponse.json().catch(() => ({}));
          toast.error('Failed to upload image: ' + (errorData.message || 'Unknown error'));
          setLoading(false);
          return;
        }
      }

      // Step 2: Create/Update course
      const courseData = {
        ...formData,
        course_image: imagePath,
        price: formData.course_type === 'free' ? 0 : parseFloat(formData.price) || 0
      };

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const url = editingCourse
        ? `${API_BASE}/admin/courses/simple/${editingCourse.id}`
        : `${API_BASE}/admin/courses/simple`;

      const method = editingCourse ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(courseData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(editingCourse ? 'Course updated successfully!' : 'Course created successfully!');
        console.log('Course saved:', result);
        resetForm();
        loadCourses();
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.message || 'Failed to save course'}`);
      }
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save course');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title || '',
      description: course.description || '',
      course_type: course.price > 0 ? 'paid' : 'free',
      price: course.price || 0,
      start_date: course.start_date ? course.start_date.split('T')[0] : '',
      end_date: course.end_date ? course.end_date.split('T')[0] : '',
      start_time: course.start_time || '',
      duration: course.duration || '',
      location_type: course.location_type || 'online',
      location: course.location || '',
      max_enrollment: course.max_enrollment || '',
      instructor: course.instructor || '',
      status: course.status || 'published'
    });
    setImagePreview(course.course_image || '');
    setShowForm(true);
  };

  const handleDelete = async (courseId) => {
    if (!confirm('Are you sure you want to delete this course?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/courses/simple/${courseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Course deleted successfully!');
        loadCourses();
      } else {
        toast.error('Failed to delete course');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>📚 Course Management</h2>
        <button
          className="btn btn-success"
          onClick={() => setShowForm(true)}
          disabled={loading}
        >
          <FaPlus /> Add New Course
        </button>
      </div>

      {/* Course Form Modal */}
      {showForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title">
                  {editingCourse ? 'Edit Course' : 'Create New Course'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={resetForm}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    {/* Course Image */}
                    <div className="col-12">
                      <label className="form-label fw-bold">Course Image/Banner *</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      {imagePreview && (
                        <div className="mt-2 position-relative">
                          <img
                            src={imagePreview.startsWith('data:') || imagePreview.startsWith('blob:') ? imagePreview : `${process.env.REACT_APP_BACKEND_URL || ''}${imagePreview}`}
                            alt="Preview"
                            className="img-thumbnail"
                            style={{ maxHeight: '200px', maxWidth: '100%' }}
                            onError={(e) => {
                              console.error('Image preview load error:', e);
                              toast.error('Failed to load image preview');
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview('');
                            }}
                            title="Remove image"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <div className="col-12">
                      <label className="form-label fw-bold">Course Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Description */}
                    <div className="col-12">
                      <label className="form-label fw-bold">Description *</label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows="3"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                      ></textarea>
                    </div>

                    {/* Course Type */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Course Type *</label>
                      <select
                        className="form-select"
                        name="course_type"
                        value={formData.course_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="free">Free (Members Only)</option>
                        <option value="paid">Paid (Members & Public)</option>
                      </select>
                    </div>

                    {/* Price */}
                    {formData.course_type === 'paid' && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Price (USD) *</label>
                        <input
                          type="number"
                          className="form-control"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    )}

                    {/* Start Date */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Start Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* End Date */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">End Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Start Time */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Start Time *</label>
                      <input
                        type="time"
                        className="form-control"
                        name="start_time"
                        value={formData.start_time}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Duration */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Duration (hours) *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="duration"
                        value={formData.duration}
                        onChange={handleInputChange}
                        min="0"
                        step="0.5"
                        required
                      />
                    </div>

                    {/* Location Type */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Location Type *</label>
                      <select
                        className="form-select"
                        name="location_type"
                        value={formData.location_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="online">Online</option>
                        <option value="onsite">Onsite</option>
                      </select>
                    </div>

                    {/* Location */}
                    {formData.location_type === 'onsite' && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Location *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="e.g., Conference Hall, City"
                          required
                        />
                      </div>
                    )}

                    {/* Instructor */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Instructor *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="instructor"
                        value={formData.instructor}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Max Enrollment */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Max Enrollment</label>
                      <input
                        type="number"
                        className="form-control"
                        name="max_enrollment"
                        value={formData.max_enrollment}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="Leave empty for unlimited"
                      />
                    </div>

                    {/* Status */}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Status *</label>
                      <select
                        className="form-select"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (editingCourse ? 'Update Course' : 'Create Course')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Courses Table */}
      <div className="card">
        <div className="card-body">
          {loading && <div className="text-center py-3">Loading...</div>}

          {!loading && courses.length === 0 && (
            <div className="text-center py-5 text-muted">
              <FaImage size={50} className="mb-3" />
              <p>No courses found. Click "Add New Course" to create one.</p>
            </div>
          )}

          {!loading && courses.length > 0 && (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Location</th>
                    <th>Enrolled</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map(course => (
                    <tr key={course.id}>
                      <td>
                        {course.course_image ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL || ''}${course.course_image}`}
                            alt={course.title}
                            style={{ width: '60px', height: '40px', objectFit: 'cover' }}
                            className="rounded"
                          />
                        ) : (
                          <div style={{ width: '60px', height: '40px', backgroundColor: '#ddd' }} className="rounded d-flex align-items-center justify-content-center">
                            <FaImage />
                          </div>
                        )}
                      </td>
                      <td>{course.title}</td>
                      <td>
                        <span className={`badge ${course.price > 0 ? 'bg-primary' : 'bg-success'}`}>
                          {course.price > 0 ? 'Paid' : 'Free'}
                        </span>
                      </td>
                      <td>${course.price || 0}</td>
                      <td>
                        {course.start_date ? new Date(course.start_date).toLocaleDateString() : '-'}
                      </td>
                      <td>{course.start_time || '-'}</td>
                      <td>
                        <span className={`badge ${course.location_type === 'online' ? 'bg-info' : 'bg-warning'}`}>
                          {course.location_type === 'online' ? 'Online' : course.location || 'Onsite'}
                        </span>
                      </td>
                      <td>{course.enrollment_count || 0} / {course.max_enrollment || '∞'}</td>
                      <td>
                        <span className={`badge ${course.status === 'published' ? 'bg-success' : 'bg-secondary'}`}>
                          {course.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary me-1"
                          onClick={() => handleEdit(course)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(course.id)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleCourseManagement;
